import { distinctFailures, lineClassifier } from '@qim/shared'
import type { ClassifiedText, RepeatedFailure } from '@qim/shared'

/**
 * A run's log, held so that it can be folded, filtered and searched (IM-166).
 *
 * The classification is `lineClassifier` from `shared` — the same one the
 * summary card reads — so the viewer and the summary cannot disagree about
 * which line is a resource header. Nothing here matches a pattern of its own.
 *
 * ## Held as columns, not as line objects
 *
 * IM-83's lesson was that a line object costs about four times the text it
 * carries. So a line is an index into parallel arrays of strings and one byte
 * of flags, and a fold is a pair of numbers. The strings are shared where they
 * can be: four lines in five carry no escape, `stripAnsi` hands back the very
 * string it was given, and the raw copy kept for the download is that same
 * string rather than a second one.
 *
 * ## Append-only, and classified once
 *
 * A page is classified as it arrives, by one classifier that keeps its memory
 * between pages — so a body that starts on one page and ends on the next is one
 * fold, and nothing already classified is read again. Every index is absolute
 * (the line's position in the whole log), which is what lets a fold someone
 * expanded stay expanded while a live run keeps appending under it.
 */

/** What a line is, as bits: a line can be a change and progress at once. */
export const LINE = {
  error: 1,
  warning: 2,
  change: 4,
  progress: 8,
  tick: 16,
  body: 32,
  header: 64,
} as const

export const LOG_FILTERS = ['error', 'warning', 'change', 'progress'] as const
export type LogFilter = (typeof LOG_FILTERS)[number]

/**
 * How many lines are held in the browser.
 *
 * It was 5,000 until IM-166, and on a real apply that cap was the bug: the log
 * is 62,388 lines, so the panel held the last twelfth of it and the plan scrolled
 * out of reach entirely. The cap existed for two costs that were really one —
 * memory, and the size of the rendered `<pre>` — and folding separates them.
 * What is held is text; what is rendered is the folded view, 2,640 lines for
 * that apply, however long the bodies.
 *
 * Measured on that apply in Chrome: held in about 10MB of heap (4.4MB of text,
 * most of it one string shared by the display and raw copies), classified in
 * ~40ms, and the folded view is 1,532 elements. So the bound moves to "a log
 * about three times the longest real one": 200,000 lines is ~15MB of text,
 * still a bounded cost whatever the run does. A run that talks for longer loses
 * its oldest lines — on a boundary outside any body, so no fold is left
 * without its header — and the panel says how many.
 */
export const MAX_HELD_LINES = 200_000

const bitsOf = (line: ClassifiedText): number => {
  const frame = line.frame
  let bits = 0
  if (frame?.severity === 'error') bits |= LINE.error
  if (frame?.severity === 'warning') bits |= LINE.warning
  switch (line.kind) {
    case 'error':
      return bits | LINE.error
    case 'warning':
      return bits | LINE.warning
    case 'installer':
      if (line.level === 'ERROR') return bits | LINE.error
      if (line.level === 'WARN' || line.level === 'WARNING') return bits | LINE.warning
      return bits
    case 'resource-header':
      return bits | LINE.change | LINE.header
    case 'plan-summary':
    case 'apply-summary':
    case 'plan-start':
      return bits | LINE.change
    case 'progress-complete':
      return bits | LINE.change | LINE.progress
    case 'progress-start':
    case 'installer-phase':
      return bits | LINE.progress
    case 'progress-tick':
      return bits | LINE.tick
    case 'attribute-body':
      return bits | LINE.body
    default:
      return bits
  }
}

/** A line as the panel shows it: the wall-clock time, then the stripped text. */
export interface LogModel {
  /** Absolute index one past the last line ever received. */
  readonly end: number
  /** Absolute index of the oldest line still held; above 0 once trimmed. */
  readonly start: number
  /** `14:12:03  <message>`, escapes removed. */
  display: (index: number) => string
  /** The message exactly as the customer's log group holds it. */
  raw: (index: number) => string
  flags: (index: number) => number
  /** Where a header's body ends (exclusive), or `undefined` if it is not a header. */
  foldEnd: (header: number) => number | undefined
  /** The header whose body holds this line, if any. */
  headerOf: (index: number) => number | undefined
  /** Error heads, in order: what "jump to error" moves between. */
  readonly errors: readonly number[]
  /** How many held lines carry each bit. */
  count: (bit: number) => number
  /** Every distinct Terraform error, from the frames seen so far. */
  readonly failures: readonly RepeatedFailure[]
  push: (lines: readonly { readonly at: string; readonly message: string }[]) => void
  /** Held lines matching `query`, case-insensitively, including folded ones. */
  search: (query: string, limit?: number) => readonly number[]
  /** Every held line, formatted, one per line — the raw view. */
  displayText: () => string
  /** Every held line exactly as written — the download. */
  rawText: () => string
}

const escapeRegExp = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

export const createLogModel = (maxLines = MAX_HELD_LINES): LogModel => {
  const classify = lineClassifier()
  let start = 0
  let times: string[] = []
  let texts: string[] = []
  let raws: string[] = []
  let flags = new Uint8Array(1024)
  /** Header indices, ascending, and where each one's body ends. */
  let headers: number[] = []
  let foldEnds: number[] = []
  let errors: number[] = []
  const counts = new Map<number, number>()
  /** The frame lines, which are what the failure headline reads. Tens, not thousands. */
  let frameLines: string[] = []
  let failures: readonly RepeatedFailure[] = []
  /** A `╷` whose severity arrives on the next line. */
  let pendingOpen: number | undefined
  let open: { header: number; slot: number } | undefined

  const bump = (bits: number, by: number): void => {
    for (const bit of Object.values(LINE)) {
      if ((bits & bit) !== 0) counts.set(bit, (counts.get(bit) ?? 0) + by)
    }
  }

  const setFlags = (index: number, bits: number): void => {
    const slot = index - start
    if (slot >= flags.length) {
      const grown = new Uint8Array(Math.max(flags.length * 2, slot + 1))
      grown.set(flags)
      flags = grown
    }
    const before = flags[slot] ?? 0
    if (before !== 0) bump(before, -1)
    flags[slot] = bits
    bump(bits, 1)
  }

  /**
   * Drops the oldest lines once past the cap, on a boundary that is not inside
   * a body — so no fold is left without its header.
   */
  const trim = (): void => {
    if (texts.length <= maxLines) return
    const end = start + texts.length
    // Keep a tenth under the cap, so a live run past it is not a splice per page.
    let cut = end - (maxLines - Math.floor(maxLines / 10))
    while (cut < end && ((flags[cut - start] ?? 0) & LINE.body) !== 0) cut += 1
    const drop = cut - start
    for (let slot = 0; slot < drop; slot += 1) bump(flags[slot] ?? 0, -1)
    times = times.slice(drop)
    texts = texts.slice(drop)
    raws = raws.slice(drop)
    flags = flags.slice(drop)
    const keep = headers.findIndex((h) => h >= cut)
    headers = keep === -1 ? [] : headers.slice(keep)
    foldEnds = keep === -1 ? [] : foldEnds.slice(keep)
    errors = errors.filter((e) => e >= cut)
    if (pendingOpen !== undefined && pendingOpen < cut) pendingOpen = undefined
    open =
      open !== undefined && headers[headers.length - 1] === open.header
        ? { header: open.header, slot: headers.length - 1 }
        : undefined
    start = cut
  }

  const model: LogModel = {
    get end() {
      return start + texts.length
    },
    get start() {
      return start
    },
    display: (index) => `${times[index - start] ?? ''}  ${texts[index - start] ?? ''}`,
    raw: (index) => raws[index - start] ?? '',
    flags: (index) => flags[index - start] ?? 0,
    foldEnd: (header) => {
      const at = binarySearch(headers, header)
      return at === -1 ? undefined : foldEnds[at]
    },
    headerOf: (index) => {
      // The last header at or before this line, if its body reaches it.
      let lo = 0
      let hi = headers.length - 1
      let found = -1
      while (lo <= hi) {
        const mid = (lo + hi) >> 1
        if ((headers[mid] ?? 0) < index) {
          found = mid
          lo = mid + 1
        } else hi = mid - 1
      }
      if (found === -1) return undefined
      return index < (foldEnds[found] ?? 0) ? headers[found] : undefined
    },
    get errors() {
      return errors
    },
    count: (bit) => counts.get(bit) ?? 0,
    get failures() {
      return failures
    },
    push: (lines) => {
      let framed = false
      for (const line of lines) {
        const index = start + texts.length
        const classified = classify(line.message)
        const bits = bitsOf(classified)
        times.push(line.at.slice(11, 19))
        texts.push(classified.text)
        raws.push(line.message)
        setFlags(index, bits)

        if (classified.frame !== undefined) {
          framed = true
          frameLines.push(classified.text)
          if (classified.frame.part === 'open') pendingOpen = index
          else if (pendingOpen !== undefined && classified.frame.severity !== undefined) {
            // The `╷` belongs to the block its head names.
            const severity = classified.frame.severity === 'error' ? LINE.error : LINE.warning
            setFlags(pendingOpen, (flags[pendingOpen - start] ?? 0) | severity)
            pendingOpen = undefined
          }
        }
        if (
          classified.kind === 'error' ||
          (classified.kind === 'installer' && (bits & LINE.error) !== 0)
        ) {
          errors.push(index)
        }

        if ((bits & LINE.header) !== 0) {
          headers.push(index)
          foldEnds.push(index + 1)
          open = { header: index, slot: headers.length - 1 }
        } else if ((bits & LINE.body) !== 0 && open !== undefined) {
          foldEnds[open.slot] = index + 1
        } else {
          open = undefined
        }
      }
      if (framed) {
        // Tens of lines, re-read per page that brought a frame: never O(log).
        failures = distinctFailures(frameLines)
      }
      trim()
      if (frameLines.length > 5000) frameLines = frameLines.slice(-2500)
    },
    search: (query, limit = 10_000) => {
      const q = query.trim()
      if (q === '') return []
      const pattern = new RegExp(escapeRegExp(q), 'i')
      const found: number[] = []
      for (let slot = 0; slot < texts.length && found.length < limit; slot += 1) {
        if (pattern.test(texts[slot] ?? '')) found.push(start + slot)
      }
      return found
    },
    displayText: () => texts.map((text, slot) => `${times[slot] ?? ''}  ${text}`).join('\n'),
    rawText: () => raws.join('\n'),
  }
  return model
}

const binarySearch = (sorted: readonly number[], value: number): number => {
  let lo = 0
  let hi = sorted.length - 1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    const at = sorted[mid] ?? 0
    if (at === value) return mid
    if (at < value) lo = mid + 1
    else hi = mid - 1
  }
  return -1
}

// ---------------------------------------------------------------------------
// What is on screen
// ---------------------------------------------------------------------------

export interface LogView {
  /** Empty is everything. Otherwise a line is shown if it matches any. */
  readonly filters: ReadonlySet<LogFilter>
  readonly showTicks: boolean
  /** Headers whose bodies are open. */
  readonly expanded: ReadonlySet<number>
  /** A line that must be on screen whatever the filters say: a jump or a match. */
  readonly target?: number | undefined
}

/**
 * A run of consecutive lines rendered as one text node.
 *
 * Merging is what keeps the DOM small without virtualising it: a thousand
 * ordinary installer lines in a row are one span, and a 19,000-line body opened
 * on purpose is one span too. The target line is always its own segment, so it
 * can be scrolled to and marked.
 */
export type LogSegment =
  | { readonly type: 'lines'; readonly from: number; readonly to: number; readonly tone: LineTone }
  | {
      readonly type: 'header'
      readonly line: number
      readonly bodyLines: number
      readonly expanded: boolean
    }

export type LineTone = 'error' | 'warning' | 'change' | 'tick' | 'body' | 'plain'

export const toneOf = (bits: number): LineTone => {
  if ((bits & LINE.error) !== 0) return 'error'
  if ((bits & LINE.warning) !== 0) return 'warning'
  if ((bits & LINE.body) !== 0) return 'body'
  if ((bits & LINE.change) !== 0) return 'change'
  if ((bits & LINE.tick) !== 0) return 'tick'
  return 'plain'
}

export interface LogLayout {
  readonly segments: readonly LogSegment[]
  /** Lines the filters are hiding, not counting folded bodies or ticks. */
  readonly hiddenByFilter: number
  readonly hiddenTicks: number
  readonly shownLines: number
}

const FILTER_BIT: Readonly<Record<LogFilter, number>> = {
  error: LINE.error,
  warning: LINE.warning,
  change: LINE.change,
  progress: LINE.progress,
}

/**
 * The folded, filtered view, as segments.
 *
 * Linear in the lines that are *not* inside a folded body: a collapsed fold is
 * skipped in one step, so 59,000 lines of CRD schema cost 461 iterations.
 */
export const layout = (model: LogModel, view: LogView): LogLayout => {
  let mask = 0
  for (const filter of view.filters) mask |= FILTER_BIT[filter]
  const segments: LogSegment[] = []
  let hiddenByFilter = 0
  let hiddenTicks = 0
  let shownLines = 0
  let run: { from: number; tone: LineTone } | undefined

  const close = (to: number): void => {
    if (run !== undefined && to > run.from) {
      segments.push({ type: 'lines', from: run.from, to, tone: run.tone })
    }
    run = undefined
  }
  const add = (index: number, tone: LineTone): void => {
    shownLines += 1
    if (index === view.target) {
      close(index)
      segments.push({ type: 'lines', from: index, to: index + 1, tone })
      return
    }
    if (run !== undefined && run.tone !== tone) close(index)
    run ??= { from: index, tone }
  }

  const end = model.end
  let i = model.start
  while (i < end) {
    const bits = model.flags(i)
    const forced = i === view.target
    if ((bits & LINE.header) !== 0) {
      const foldEnd = model.foldEnd(i) ?? i + 1
      const targetInside = view.target !== undefined && view.target > i && view.target < foldEnd
      const passes = mask === 0 || (bits & mask) !== 0 || forced || targetInside
      if (!passes) {
        close(i)
        hiddenByFilter += foldEnd - i
        i = foldEnd
        continue
      }
      close(i)
      const expanded = view.expanded.has(i) || targetInside
      shownLines += 1
      segments.push({ type: 'header', line: i, bodyLines: foldEnd - i - 1, expanded })
      if (expanded) {
        for (let b = i + 1; b < foldEnd; b += 1) add(b, 'body')
        // Bodies are uniform, so this loop merges them into at most three segments.
        close(foldEnd)
      }
      i = foldEnd
      continue
    }
    if ((bits & LINE.tick) !== 0 && !view.showTicks && !forced) {
      close(i)
      hiddenTicks += 1
      i += 1
      continue
    }
    if (mask !== 0 && (bits & mask) === 0 && !forced) {
      close(i)
      hiddenByFilter += 1
      i += 1
      continue
    }
    add(i, toneOf(bits))
    i += 1
  }
  close(end)
  return { segments, hiddenByFilter, hiddenTicks, shownLines }
}
